import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { extractErr } from "@/lib/api";

export const isCarPartsTheme = (id) => String(id || "").toLowerCase() === "carparts";
export const themeRequiresAddon = (id) => {
  const t = String(id || "").toLowerCase();
  return t === "carparts" || t === "ondemand";
};
export const themeLabel = (id) => {
  const t = String(id || "").toLowerCase();
  if (t === "ondemand") return "Ondemand";
  if (t === "carparts") return "Car Parts";
  return t || "This";
};

const rupee2 = (n) =>
  `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function CarPartsAddonDialog({
  open,
  onOpenChange,
  mode = "signup",
  quote: quoteProp,
  onConfirm,
  onSecondary,
  confirmLabel,
  secondaryLabel,
  busy = false,
  themeId = "carparts",
}) {
  const [quote, setQuote] = useState(quoteProp || null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const tid = String(themeId || quoteProp?.theme_id || "carparts").toLowerCase();
  const label = themeLabel(tid);
  const testIds = tid === "ondemand"
    ? { dialog: "ondemand-addon-dialog", quote: "ondemand-addon-quote", total: "ondemand-addon-total", no: "ondemand-addon-no", yes: "ondemand-addon-yes" }
    : { dialog: "carparts-addon-dialog", quote: "carparts-addon-quote", total: "carparts-addon-total", no: "carparts-addon-no", yes: "carparts-addon-yes" };

  useEffect(() => {
    if (quoteProp) setQuote(quoteProp);
  }, [quoteProp]);

  useEffect(() => {
    if (!open || mode !== "signup" || quoteProp) return;
    let alive = true;
    setLoading(true);
    setErr("");
    api
      .get("/auth/signup/quote", { params: { plan_id: "website", storefront_theme: tid } })
      .then(({ data }) => {
        if (alive) setQuote(data);
      })
      .catch((e) => {
        if (alive) setErr(extractErr(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [open, mode, quoteProp, tid]);

  const addonName = quote?.addon_name || (tid === "ondemand" ? "Ondemand Service Area + Ondemand Delivery Panel" : "Car Parts");
  const themeAmt = quote?.plan_amount ?? quote?.base_amount;
  const addonAmt = quote?.addon_amount ?? quote?.price;
  const gstAmt = quote?.gst_amount;
  const total = quote?.total;
  const addonRows = quote?.addons?.length ? quote.addons : (addonAmt != null ? [{ name: addonName, price: addonAmt }] : []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-2xl p-0 overflow-hidden" data-testid={testIds.dialog}>
        <div className="px-6 pt-6 pb-4">
          <DialogTitle className="text-xl font-bold text-stone-900">{label} theme requires an addon</DialogTitle>
          <p className="mt-2 text-sm text-stone-600">
            This theme requires additional addon “{addonName}”
            {addonAmt != null ? <> of {rupee2(addonAmt)}</> : null}.
            {mode === "setup"
              ? " Pay now to continue onboarding, or select another theme."
              : " App building is not included. Continue to checkout with the total below."}
          </p>
          {loading ? (
            <div className="mt-5 flex items-center gap-2 text-sm text-stone-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading Super Admin price…
            </div>
          ) : err ? (
            <p className="mt-4 text-sm text-red-600">{err}</p>
          ) : quote ? (
            <div className="mt-5 rounded-xl border border-stone-200 bg-stone-50 p-4 text-sm" data-testid={testIds.quote}>
              {mode === "signup" ? (
                <div className="flex items-center justify-between text-stone-700">
                  <span>{label} Theme</span>
                  <span>{rupee2(themeAmt)}</span>
                </div>
              ) : null}
              {addonRows.map((row, i) => (
                <div key={row.id || i} className={`flex items-center justify-between text-stone-700 ${mode === "signup" || i > 0 ? "mt-1" : ""}`}>
                  <span>{row.name} Addon</span>
                  <span>{rupee2(row.price)}</span>
                </div>
              ))}
              <div className="flex items-center justify-between text-stone-700 mt-1">
                <span>GST ({quote.gst_percent || 18}%)</span>
                <span>{rupee2(gstAmt)}</span>
              </div>
              <div className="flex items-center justify-between text-stone-900 font-bold mt-2 pt-2 border-t border-dashed border-stone-300">
                <span>Total Amount</span>
                <span data-testid={testIds.total}>{rupee2(total)}</span>
              </div>
            </div>
          ) : null}
        </div>
        <div className="px-6 pb-6 flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outline"
            className="flex-1 rounded-full"
            data-testid={testIds.no}
            onClick={() => {
              if (onSecondary) onSecondary();
              else onOpenChange(false);
            }}
          >
            {secondaryLabel || (mode === "setup" ? "Select another theme" : "No, go back")}
          </Button>
          <Button
            type="button"
            disabled={busy || loading || !quote}
            className="flex-1 rounded-full bg-[#FF6B00] hover:bg-[#e86000] text-white font-bold"
            data-testid={testIds.yes}
            onClick={() => onConfirm && onConfirm(quote)}
          >
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" /> Please wait…</> : (confirmLabel || (mode === "setup" ? "Pay now" : "Yes, proceed to checkout"))}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CreateStoreWithTheme({ themeId, className, children, testId }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  if (!themeRequiresAddon(themeId)) {
    return (
      <Link to={`/signup?theme=${themeId}`} className={className} data-testid={testId}>
        {children}
      </Link>
    );
  }
  return (
    <>
      <button type="button" className={className} data-testid={testId} onClick={() => setOpen(true)}>
        {children}
      </button>
      <CarPartsAddonDialog
        open={open}
        onOpenChange={setOpen}
        mode="signup"
        themeId={themeId}
        onConfirm={() => nav(`/signup?theme=${themeId}`)}
      />
    </>
  );
}
